/**
 * CourseDetailTabs — vertical tabs (≥md) / horizontal scroll (<md) for course detail page.
 * Tabs: Tổng quan | Units & Lessons | Bài tập tổng.
 * Syncs active tab to ?tab= query param via URL hash to avoid TanStack Router
 * strict-type conflicts when used outside the route file.
 */
import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CourseOverviewTab } from './course-overview-tab';
import { CourseUnitsTab } from './course-units-tab';
import { CourseAssignmentsTab } from './course-assignments-tab';

const TABS = [
  { value: 'overview', label: 'Tổng quan' },
  { value: 'units', label: 'Units & Bài học' },
  { value: 'assignments', label: 'Bài tập tổng' },
] as const;

type TabValue = (typeof TABS)[number]['value'];

const VALID_TABS = TABS.map((t) => t.value);

function getTabFromUrl(): TabValue {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  return (VALID_TABS as string[]).includes(tab ?? '') ? (tab as TabValue) : 'overview';
}

function setTabInUrl(tab: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', tab);
  window.history.replaceState(null, '', url.toString());
}

interface CourseDetailTabsProps {
  courseId: string;
}

export function CourseDetailTabs({ courseId }: CourseDetailTabsProps) {
  const [activeTab, setActiveTab] = useState<TabValue>(getTabFromUrl);

  useEffect(() => {
    // Sync tab if user navigates with browser back/forward
    function onPopState() {
      setActiveTab(getTabFromUrl());
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function handleTabChange(value: string) {
    setActiveTab(value as TabValue);
    setTabInUrl(value);
  }

  // Vertical sidebar tabs on md+, stacks above content < md.
  // Match the existing pattern used in /admin/classes/$classId — Radix
  // requires orientation="vertical" + variant="line" to render correctly;
  // mixing horizontal orientation with custom flex utilities causes the
  // triggers to overlap.
  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      orientation="vertical"
      className="flex-col gap-4 md:flex-row md:gap-6"
    >
      <TabsList
        variant="line"
        className="w-full shrink-0 md:w-48 md:border-r md:pr-2"
      >
        {TABS.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="overview" className="mt-0">
        <CourseOverviewTab courseId={courseId} />
      </TabsContent>

      <TabsContent value="units" className="mt-0">
        <CourseUnitsTab courseId={courseId} />
      </TabsContent>

      <TabsContent value="assignments" className="mt-0">
        <CourseAssignmentsTab courseId={courseId} />
      </TabsContent>
    </Tabs>
  );
}
